import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router';
import {
  appRoutes,
  route,
  useCanUseAppGuard,
  usePostHogIdentify,
  usePresenceHeartbeatManager,
  useSentryUser,
} from '@hero/web-hooks';
import { isFeatureEnabled } from '@hero/web-context';
import { Feature } from '@hero/web-components';
import { BillingIcon, HandCoinsIcon, LoadsNavIcon, NewLoadsNavIcon } from '@hero/web-icons';
import ProtectedRoute from '../components/features/Auth/ProtectedRoute';
import PublicOnlyRoute from '../components/features/Auth/PublicOnlyRoute';
import { APP_CONFIG } from '../config';
import AuthLayout from '../layouts/AuthLayout';
import DashboardLayout from '../layouts/DashboardLayout';
import OrderLayout from '../layouts/OrderLayout';
import Profile from '../pages/Applications/Profile';
import ChoosePerson from '../pages/Auth/ChoosePerson';
import Register from '../pages/Auth/Register';
import RegisterSuccess from '../pages/Auth/RegisterSuccess';
import RegisterVerifyNum from '../pages/Auth/RegisterVerifyNum';
import Unauthorized from '../pages/Auth/Unauthorized';
import AccountBlocked from '../pages/Auth/AccountBlocked';
import OrgBlocked from '../pages/Auth/OrgBlocked';
import DashboardHome from '../pages/ClientDashboard/DashboardHome';
import DashboardWallet from '../pages/ClientDashboard/DashboardWallet';
import AccountsPage from '../pages/Accounts/AccountsPage';
import CRMWorkspacePage from '../pages/CRM/CRMWorkspacePage';
import FleetPage from '../pages/Fleet/FleetPage';
import NewLoadsPage from '../pages/NewLoads/NewLoadsPage';
import MyOrdersPage from '../pages/MyOrders/MyOrdersPage';
import OrdersPreviewPage from '../pages/MyOrders/preview/OrdersPreviewPage';
import CreateOrderPage from '../pages/Order/CreateOrderPage';
import SettingsPage from '../pages/Settings/SettingsPage';
import UsersPage from '../pages/Settings/UsersPage';
import PricingPage from '../pages/More/PricingPage';
import ContactsPage from '../pages/More/ContactsPage';
import PlacesPage from '../pages/More/PlacesPage';
import AppsPage from '../pages/Apps/AppsPage';
import BillingOverviewPage from '../pages/Billing/BillingOverviewPage';
import BillingPlansPage from '../pages/Billing/BillingPlansPage';
import BillingHistoryPage from '../pages/Billing/BillingHistoryPage';
import BillingDetailPage from '../pages/Billing/BillingDetailPage';
import BillingCheckoutReturnPage from '../pages/Billing/BillingCheckoutReturnPage';
import BillingAddonsPage from '../pages/Billing/BillingAddonsPage';
import FinancingCarrierPage from '../pages/Financing/FinancingCarrierPage';
import FinancingLenderViewPage from '../pages/Financing/lender-view/FinancingLenderViewPage';
import FinancingPartnerRedirect from '../pages/Financing/lender-view/FinancingPartnerRedirect';
import AIAgentPage from '../pages/AIAgent/AIAgentPage';
import PageNotFound from '../pages/PageNotFound';

function TrackPageViews() {
  const location = useLocation();

  useEffect(() => {
    if (APP_CONFIG.googleAnalytics.shouldActivate) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }
  }, [location]);

  return null;
}

/**
 * Legacy redirect: `/crm/clients/:clientId` → `/crm?clientId=:clientId`.
 * The dedicated full-client page is replaced by the master-detail
 * workspace at `/crm`, which selects clients via `?clientId=`.
 */
function ClientIdRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!isFeatureEnabled('crm_module')) {
    return <Navigate to={route('dashboard')} replace />;
  }
  if (!clientId) return <Navigate to={route('crm')} replace />;
  return <Navigate to={route('crm', { clientId })} replace />;
}

/**
 * Legacy redirect: `/settings/profile/:id` and `/applications/profile/:id`
 * → `/more/profile` (typed `settings_profile`). The `:id` segment was
 * a vestige of the legacy URL — the profile page always renders the
 * current user's own org via `useCurrentUser()`, so the id is dropped
 * on the way through.
 */
function ProfileIdRedirect() {
  return <Navigate to={route('settings_profile')} replace />;
}

const AppContent = () => {
  useSentryUser();
  usePostHogIdentify('dashboard');
  usePresenceHeartbeatManager();

  useCanUseAppGuard();

  return (
    <Routes>
      {/* Protected Dashboard Routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
        {/* Home */}
        <Route path={appRoutes.dashboard.path}>
          <Route index element={<DashboardHome />} />
        </Route>

        {/*
          Single-page publish-order flow. `CreateOrderPage` is a thin
          wrapper around the shared `<PublishOrderPage />` from
          `@hero/web-components`; that component owns the form state
          (Zustand) and wires the real API hooks (customers, saved
          locations, autocomplete, order create). Lives at the
          top-level `/dashboard/new-order` so the URL reads as the
          action; `OrderLayout` keeps the page filling the dashboard
          content area (the 60/40 form/map split is owned by the
          shared component itself).
        */}
        <Route path={appRoutes.new_order.path} element={<OrderLayout />}>
          <Route index element={<CreateOrderPage />} />
        </Route>

        {/* New Loads (Published Orders) — same guard as sidebar/banner: reactive `Feature` */}
        <Route path={appRoutes.new_loads.path}>
          <Route
            index
            element={
              <Feature
                flag="order_module"
                subFlag="sub_published_orders"
                component={NewLoadsPage}
                icon={NewLoadsNavIcon}
              />
            }
          />
        </Route>

        {/* My Orders */}
        <Route path={appRoutes.orders.path}>
          <Route
            index
            element={
              <Feature
                flag="order_module"
                subFlag="sub_my_orders"
                component={MyOrdersPage}
                icon={LoadsNavIcon}
              />
            }
          />
        </Route>

        {/*
          PREVIEW / DESIGN-REVIEW surface for the financing-style
          expandable orders layout. URL-only (`/my-orders/preview`);
          NEVER add it to the sidebar. Mounted directly without
          `<Feature>` because this is mock-only design scaffolding
          the product owner has asked us to keep parked as a
          reference until they sign off on promotion. Do NOT delete
          this route, do NOT wire it to live data, do NOT move
          `OrdersPreviewPage` into `libs/frontend/web/components`.

          Rules + promotion path:
          `apps/carrier-dashboard/src/pages/MyOrders/preview/README.md`.
        */}
        <Route
          path={appRoutes.orders_preview.path}
          element={<OrdersPreviewPage />}
        />

        {/* Fleet (Vehicles + Drivers) */}
        <Route path={appRoutes.fleet.path}>
          <Route
            index
            element={
              !isFeatureEnabled('fleet_module') ? (
                <Navigate to={route('dashboard')} replace />
              ) : (
                <FleetPage />
              )
            }
          />
        </Route>

        {/* CRM (Client master-detail workspace) */}
        <Route path={appRoutes.crm.path}>
          <Route
            index
            element={
              !isFeatureEnabled('crm_module') ? (
                <Navigate to={route('dashboard')} replace />
              ) : (
                <CRMWorkspacePage />
              )
            }
          />
          <Route path="clients/:clientId" element={<ClientIdRedirect />} />
        </Route>

        {/* Wallet */}
        <Route path={appRoutes.wallet.path}>
          <Route
            index
            element={
              !isFeatureEnabled('wallet_module') ? (
                <Navigate to={route('dashboard')} replace />
              ) : (
                <DashboardWallet />
              )
            }
          />
        </Route>

        {/*
          Bilateral Accounts — the replacement surface for `/wallet`.
          Lists every counterparty the viewer has a balance with;
          per-account drill-in opens the AccountDrawer (mounted on
          this page) which then mounts the bilateral InvoiceDrawer
          and AccountTransactionDrawer for cross-link navigation.
        */}
        <Route path={appRoutes.accounts.path}>
          <Route index element={<AccountsPage />} />
        </Route>

        {/*
          Billing & subscription — Hero-as-seller pages mounted under
          `/billing`. Gated by `billing_module`: `<Feature>` resolves
          the flag reactively and falls back to default `coming_soon`
          / `maintenance` / `disabled` screens so the whole surface
          can be rolled back from PostHog without a deploy. The More
          card visibility is gated by the same flag in
          `staticData.ts` / `ApplicationsCards.tsx`.
        */}
        <Route
          path={appRoutes.billing.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingOverviewPage}
              icon={BillingIcon}
            />
          }
        />
        <Route
          path={appRoutes.billing_change_plan.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingPlansPage}
              icon={BillingIcon}
            />
          }
        />
        <Route
          path={appRoutes.billing_addons.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingAddonsPage}
              icon={BillingIcon}
            />
          }
        />
        <Route
          path={appRoutes.billing_history.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingHistoryPage}
              icon={BillingIcon}
            />
          }
        />
        <Route
          path={appRoutes.billing_invoice.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingDetailPage}
              icon={BillingIcon}
            />
          }
        />
        {/*
          Payment confirmation page — landing target for Moyasar's
          `success_url` / `callback_url` after hosted checkout. Reads
          `?id=<moyasarPaymentId>` from the URL, polls the BTx until
          terminal status, and renders verifying / success / failed /
          pending with clear navigation back to home or billing. See
          `useSubscriptionPayManager.buildReturnUrls` and
          `useBillingPayManager.buildReturnUrl` for the wiring side.
        */}
        <Route
          path={appRoutes.billing_checkout_return.path}
          element={
            <Feature
              flag="billing_module"
              component={BillingCheckoutReturnPage}
              icon={BillingIcon}
            />
          }
        />

        {/*
          Embedded financing — carrier landing at /financing.
          `<Feature>` resolves `financing_module` reactively and falls
          back to the default `coming_soon` / `maintenance` /
          `disabled` screens when the flag's payload status switches.
          The public lender inbox at /financing/lender lives outside
          this protected block so unauthenticated lenders can open it.
        */}
        <Route
          path={appRoutes.financing.path}
          element={
            <Feature
              flag="financing_module"
              component={FinancingCarrierPage}
              icon={HandCoinsIcon}
            />
          }
        />

        {/* More (formerly "Settings") — shell only; inner screens use their own flags */}
        <Route
          path={appRoutes.settings.path}
          element={
            !isFeatureEnabled('more_screen') ? (
              <Navigate to={route('dashboard')} replace />
            ) : (
              <Outlet />
            )
          }>
          <Route index element={<SettingsPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="places" element={<PlacesPage />} />
          <Route
            path="apps"
            element={
              !isFeatureEnabled('apps_module') ? (
                <Navigate to={route('settings')} replace />
              ) : (
                <AppsPage />
              )
            }
          />
          <Route path="clients" element={<Navigate to={route('crm')} replace />} />
        </Route>

        {/*
          Full-page AI assistant. Reuses the same `<ChatPanel />` the
          floating launcher mounts so the surface stays a single source
          of truth. Gated behind `ai_agent_module` (sub_full_page) —
          while the assistant is in pre-MVP staging the route 302s back
          to the dashboard outside the rollout cohort. Long-term this
          page is slated to grow into the carrier-dashboard homepage
          replacement, see `docs/ai/architecture.md`.
        */}
        <Route
          path={appRoutes.ai_agent.path}
          element={
            !isFeatureEnabled('ai_agent_module', 'sub_full_page') ? (
              <Navigate to={route('dashboard')} replace />
            ) : (
              <AIAgentPage />
            )
          }
        />
      </Route>

      {/* Block gates — full-screen UX; no server-side API cutoff */}
      <Route path={appRoutes.account_blocked.path} element={<AccountBlocked />} />
      <Route
        path={appRoutes.org_blocked.path}
        element={
          <ProtectedRoute>
            <OrgBlocked />
          </ProtectedRoute>
        }
      />

      {/* Public Auth Routes - Only accessible when not authenticated */}
      <Route
        element={
          <PublicOnlyRoute>
            <AuthLayout />
          </PublicOnlyRoute>
        }>
        <Route path={appRoutes.auth_register.path} element={<Register />} />
        <Route path={appRoutes.auth_verify_otp.path} element={<RegisterVerifyNum />} />
        <Route path={appRoutes.auth_choose_person.path} element={<ChoosePerson />} />
        <Route path={appRoutes.auth_verified.path} element={<RegisterSuccess />} />
        <Route path={appRoutes.auth_unauthorized.path} element={<Unauthorized />} />
      </Route>

      {/*
        Public lender-view of a single application. Path carries the
        application `_id` as `:appId`; the lender identity rides in
        `?i=<lenderId>`. No JWT, no token. Contact masking is enforced
        server-side; the frontend simply renders what it gets. See
        `docs/financing/partner-access.md` for the v0 trust model.
      */}
      <Route
        path={appRoutes.financing_lender_view.path}
        element={
          <Feature
            flag="financing_module"
            component={FinancingLenderViewPage}
            icon={HandCoinsIcon}
          />
        }
      />
      {/*
        Legacy `/financing/partner/:appId?p=` alias. Kept one release
        so in-flight lender invite emails redirect to the new
        `/financing-application/:appId?i=` page instead of 404ing.
        Drop this and the matching backend redirect after the open-
        link window has cycled.
      */}
      <Route
        path={appRoutes.financing_partner.path}
        element={<FinancingPartnerRedirect />}
      />

      {/* Root Redirect */}
      <Route path="/" element={<Navigate to={route('dashboard')} replace />} />

      {/* Legacy route redirects */}
      <Route path="/trucks" element={<Navigate to={route('fleet')} replace />} />
      <Route path="/jobs" element={<Navigate to={route('new_loads')} replace />} />
      <Route path="/loads" element={<Navigate to={route('orders')} replace />} />
      <Route path="/users" element={<Navigate to={route('settings_users')} replace />} />
      <Route path="/settings" element={<Navigate to={route('settings')} replace />} />
      <Route
        path="/settings/users"
        element={<Navigate to={route('settings_users')} replace />}
      />
      <Route path="/settings/profile/:id" element={<ProfileIdRedirect />} />
      <Route path="/applications" element={<Navigate to={route('settings')} replace />} />
      <Route
        path="/applications/drivers"
        element={<Navigate to={route('settings_users')} replace />}
      />
      <Route path="/applications/profile/:id" element={<ProfileIdRedirect />} />

      {/* NotFound */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <TrackPageViews />
      <AppContent />
    </BrowserRouter>
  );
}